// src/components/SocialProof/SocialProof.jsx
"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import styles from "./SocialProof.module.css";

const DEFAULT_TESTIMONIALS = [
  {
    quote:
      "This was my daughter's first exposure to coding, and this class made her fall in love with it.",
    name: "Amanda U",
    rating: 5,
  },
  {
    quote: "thank you for making something challenging more accessible",
    name: "Carlos S",
    rating: 5,
  },
  {
    quote: "Thank you so much for such an amazing course 🫶",
    name: "Daria H",
    rating: 5,
  },
  {
    quote: "A great learning experience.",
    name: "Boi E",
    rating: 5,
  },
  {
    quote:
      "He is awesome. He makes sure his students learn everyday activity even it means to go over the time.",
    name: "Kiersty Q",
    rating: 5,
  },
  {
    quote:
      "Frankly, I'm SHOCKED by how good this class was. This was our first time trying an online class like this for our 10-year old, and it was a success on every. single. front. Andrew is a MASTER - how he kept the kids engaged, boosted their self-esteem, AND taught them coding...I'll never know. Cheers. Seriously.",
    name: "Ken S",
    rating: 5,
  },
];

const DEFAULT_STATS = [
  { value: 1376, display: "1,376", label: "Students Taught"},
  { value: 3727.2, display: "3,727.2", label: "Hours Taught" },
];

// Animates a number from 0 to `target` over `duration` ms
function useCountUp(target, duration = 1400, shouldStart = false) {
  const [count, setCount] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (!shouldStart) return;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * eased));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, shouldStart]);

  return count;
}

function StatItem({ stat, animate }) {
  const count = useCountUp(stat.value, 1400, animate);
  const displayed = animate
    ? count.toLocaleString()
    : stat.display;

  return (
    <div className={styles.statItem}>
      <span className={styles.statValue}>
        {displayed}
        <span className={styles.statSuffix}>{stat.suffix}</span>
      </span>
      <span className={styles.statLabel}>{stat.label}</span>
    </div>
  );
}

function StatsBar({ stats = DEFAULT_STATS }) {
  const ref = useRef(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.statsBar} ref={ref} aria-label="Impact statistics">
      {stats.map((stat, i) => (
        <React.Fragment key={stat.label}>
          <StatItem stat={stat} animate={animate} />
          {i < stats.length - 1 && (
            <div className={styles.statDivider} aria-hidden="true" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Stars({ rating = 5 }) {
  const safe = Math.max(0, Math.min(5, rating));
  return (
    <div className={styles.stars} aria-label={`${safe} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={`${styles.star} ${i < safe ? styles.starOn : styles.starOff}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function makePreview(text, maxChars) {
  const trimmed = String(text ?? "").trim();
  if (trimmed.length <= maxChars) return { preview: trimmed, isTruncated: false };

  const slice = trimmed.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(" ");
  const preview = (lastSpace > 60 ? slice.slice(0, lastSpace) : slice).trim();

  return {
    preview: preview.replace(/[,\s]+$/g, "").trim() + "…",
    isTruncated: true,
  };
}

function TestimonialQuote({ text, maxChars = 150 }) {
  const [expanded, setExpanded] = useState(false);
  const { preview, isTruncated } = useMemo(
    () => makePreview(text, maxChars),
    [text, maxChars]
  );

  return (
    <blockquote className={styles.quote}>
      <span> 
        "{expanded || !isTruncated ? String(text).trim() : preview}"
      </span>

      {isTruncated && (
        <>
          {" "}
          <button
            type="button"
            className={styles.readMoreInlineBtn}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        </>
      )}
    </blockquote>
  );
}

function useIsMobile(breakpointPx = 640) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpointPx);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpointPx]);

  return isMobile;
}

export default function SocialProof({
  heading = "Loved by parents who want real learning",
  testimonials = DEFAULT_TESTIMONIALS,
  stats = DEFAULT_STATS,
  maxQuoteChars = 100,

  mobileDefaultCount = 3,
  desktopDefaultCount = 6,

  allReviewsHref = "/reviews",
}) {
  const isMobile = useIsMobile(640);
  const defaultCount = isMobile ? mobileDefaultCount : desktopDefaultCount;

  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShowAll(false);
  }, [isMobile]);

  const visible = showAll ? testimonials : testimonials.slice(0, defaultCount);
  const hiddenCount = Math.max(0, testimonials.length - visible.length);

  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
      <header className={styles.header}>
      <h2 className={styles.h2}>{heading}</h2>
      </header>

        {/* ── Stats bar ── */}
        <StatsBar stats={stats} />
        <div className={styles.grid}>
          {visible.map((t, idx) => (
            <figure key={`${t.name}-${idx}`} className={styles.card}>
              <Stars rating={t.rating} />
              <TestimonialQuote text={t.quote} maxChars={maxQuoteChars} />
              <figcaption className={styles.caption}>
                <div className={styles.name}>{t.name}</div>
              </figcaption>
            </figure>
          ))}
        </div>

        {isMobile && testimonials.length > mobileDefaultCount && (
          <div className={styles.moreRow}>
            {!showAll ? (
              <button
                type="button"
                className={styles.showAllBtn}
                onClick={() => setShowAll(true)}
              >
                Show {hiddenCount} more
              </button>
            ) : (
              <button
                type="button"
                className={styles.showAllBtn}
                onClick={() => setShowAll(false)}
              >
                Show less
              </button>
            )}
          </div>
        )}

        <div className={styles.ctaRow}>
          <a className={styles.secondaryBtn} href="/catalog">
            Start with a FREE class
          </a>
        </div>
      </div>
    </section>
  );
}